import {createSlice} from '@reduxjs/toolkit';
import {setError} from '../error/errorSlice';
import {setCheckoutSpinner} from '../spinner/spinnerSlice';
import {calcPercent} from '../../common/helper';
import {sendAnalyticsEvents, sendRevenueEvent} from '../../analytics/common';
import {EVENTS} from '../../analytics/events';
import {setEventsData} from '../onboarding/onboardingSlice';
import {getPollingID, getSubscriptionID, sendStripeReq, subscribeToPriceChange} from './fetch';
import { setAlternativeStripeDataAsMain } from '../auth/authSlice';


export const paywallSlice = createSlice({
    name: 'paywall',
    initialState: {
        plan: '',
        cardholderName: '',
        token: null,
        isSuccess: null,
        isFormValid: null,
        stripeToken: null,
        id: null,
        finalPrice: null,
        discountPercentage: null,
        upsellData: null,
        stripeNonce: '',
        upsellPlan: '',
    },
    reducers: {
        setPlan: (state, action) => {
            state.plan = action.payload;
        },
        setCardholderName: (state, action) => {
            state.cardholderName = action.payload;

        },
        setRequestResult: (state, action) => {
            const {isSuccess, id, finalPrice, regularPrice} = action.payload;

            state.isSuccess = isSuccess;
            state.id = id;
            state.finalPrice = finalPrice;
            state.regularPrice = regularPrice;
        },
        setDiscountPercentage: (state, action) => {
            const {finalPrice, regularPrice} = action.payload;
            state.discountPercentage = calcPercent((regularPrice - finalPrice), regularPrice);
        },
        setFormValidity: (state, action) => {
            state.isFormValid = action.payload;
        },
        setStripeToken: (state, action) => {
            state.stripeToken = action.payload;
        },
        setStripeNonce: (state, action) => {
            state.stripeNonce = action.payload;
        },
        clearPaywallData: (state, action) => {
            state.plan = '';
            state.cardholderName = '';
            state.token = null;
            state.isSuccess = null;
            state.isFormValid = null;
            state.stripeToken = null;
            state.id = null;
            state.finalPrice = null;
        },
        setUpsellData: (state, action) => {

            state.upsellData = {
                success: action.payload?.isSuccess,
                finalPrice: action.payload?.finalPrice,
                regularPrice: action.payload?.regularPrice
            };
        },
        setUpsellPlan: (state, action) => {
            state.upsellPlan = action.payload;
        },
    },
});

export const {
    setPlan,
    setCardholderName,
    setDiscountPercentage,
    setRequestResult,
    setFormValidity,
    clearPaywallData,
    setUpsellData,
    setStripeNonce,
    setUpsellPlan
} = paywallSlice.actions;

const sendServerReq = async (stripe, paymentMethodObj, plan, token, purchaseAnalyticsData, amplitudeEventData, onError, onSuccess, eventId, upsell, payment_intent_id) => {
    const {stripeNonce, methodName} = paymentMethodObj;
    const pollingID = await getPollingID(stripeNonce, plan, token, purchaseAnalyticsData, amplitudeEventData, onError, eventId, upsell, payment_intent_id);
    const purchaseRequest = await subscribeToPriceChange(pollingID);
    const {status, errors, data} = purchaseRequest;

    if (status) {
        if (status === 'requires_action' && data && data.payment_intent_secret) {
            const cardPaymentSecret = data.payment_intent_secret;
            const cardPaymentData = methodName ? {payment_method: stripeNonce} : {setup_future_usage: 'off_session'};
            const cardPaymentOptions = methodName ? {handleActions: false} : {};
            const {error: errorAction, paymentIntent} = await stripe.confirmCardPayment(cardPaymentSecret,cardPaymentData,cardPaymentOptions);

            if (errorAction) {
                if (methodName) {
                    onError(errorAction.message, data);
                } else {
                    await sendServerReq(stripe, paymentMethodObj, plan, token, purchaseAnalyticsData, amplitudeEventData, onError, onSuccess, eventId, upsell, data.payment_intent_id);
                }
                return;
            }   
            
            if (methodName) {
                if (paymentIntent.status === 'requires_action') {
                    const {error} = await stripe.confirmCardPayment(data.payment_intent_secret);
                    if (error) {
                        onError(error, data);
                        return;
                    }
                }
                onSuccess();
            }   

            await sendServerReq(stripe, paymentMethodObj, plan, token, purchaseAnalyticsData, amplitudeEventData, onError, onSuccess, eventId, upsell, paymentIntent.id);

        } else {
            const {subscription_id, price_with_discount, regular_price} = purchaseRequest;

            return onSuccess(subscription_id, price_with_discount, regular_price);
        }
    }

    if (errors) {
        if (status === 'requires_action') return;

        if (errors.messages.includes('insufficient')) {
            sendAnalyticsEvents(EVENTS.paymentDeclined, amplitudeEventData);
        }

        onError(errors.messages || 'An error has occurred', data);
    }
};

export const sendMainPurchaseRequest = (paymentMethodObj = null, stripe, card, name, plan, token, purchaseAnalyticsData, amplitudeEventData, eventId, successPayment, failedPayment) => async dispatch => {
    const onError = (data, errorData) => {
        if (failedPayment) failedPayment();

        let txt = data;
        if (typeof txt !== 'string') txt = data.message;
        if (typeof txt !== 'string') {
            txt = 'Payment error';
            console.error(data);
        }
        dispatch(setEventsData({id: 'decline_code', data: errorData?.decline_code || errorData?.processor_response,}));
        sendAnalyticsEvents(EVENTS.subBuyFailed, {...amplitudeEventData, declineCode: errorData?.decline_code || errorData?.processor_response,});
        dispatch(setError({show: true, text: txt, type: 'payment'}));
        dispatch(setAlternativeStripeDataAsMain());
    };
    const onSuccess = (id, finalPrice, regularPrice) => {
        if (successPayment) successPayment();
        if (finalPrice) {
            dispatch(setDiscountPercentage({finalPrice, regularPrice}));
        }

        dispatch(setRequestResult({
            isSuccess: true,
            id,
            finalPrice,
            regularPrice
        }));

        sendRevenueEvent(plan);
        sendAnalyticsEvents(EVENTS.subscribed, amplitudeEventData);
    };

    dispatch(setCheckoutSpinner({show: true, type: 'checkout'}));

    try {
        if (paymentMethodObj) {
            dispatch(setStripeNonce(paymentMethodObj.stripeNonce));
            await sendServerReq(stripe, paymentMethodObj, plan, token, purchaseAnalyticsData, amplitudeEventData, onError, onSuccess, eventId, '', '');
        } else {
            const {paymentMethod, error} = await sendStripeReq(stripe, card, name);
            let stripeNonce = paymentMethod?.id || error?.code;

            if (error) {
                console.error(error.message);
                dispatch(setError({show: true, text: error.message, type: 'payment'}));
            } else {
                dispatch(setStripeNonce(stripeNonce));
                await sendServerReq(stripe, {stripeNonce}, plan, token, purchaseAnalyticsData, amplitudeEventData, onError, onSuccess, eventId, '', '');
            }
        }
    } finally {
        dispatch(setCheckoutSpinner({show: false, type: ''}));
    }
};

export const sendUpsellPurchaseRequest = (stripeNonce, plan, token, purchaseAnalyticsData, amplitudeEventData, sendGAEvent, eventId) => async dispatch => {
    const onError = (txt) => {
        console.log(txt);
        dispatch(setError({show: true, text: txt, type: 'payment'}));
        dispatch(setUpsellData({isSuccess: false, finalPrice: null, regularPrice: null}));
    };
    const onSuccess = (id, finalPrice, regularPrice) => {
        dispatch(setUpsellData({isSuccess: true, finalPrice, regularPrice}));

        sendAnalyticsEvents(EVENTS.upsellPurchased, amplitudeEventData);
        sendGAEvent();
    };

    dispatch(setCheckoutSpinner({show: true, type: 'checkout'}));

    try {
        await sendServerReq(null, { stripeNonce }, plan, token, purchaseAnalyticsData, amplitudeEventData, onError, onSuccess, eventId, 'upsell');
    } finally {
        dispatch(setCheckoutSpinner({show: false, type: ''}));
    }
};

export const paypalPurchase = (nonce, plan, token, analyticsParams, eventsData, eventId) => async dispatch => {
    const onError = (data) => {
        let txt = data;
        if (typeof txt !== 'string') txt = data.message;
        if (typeof txt !== 'string') {
            txt = 'Payment error';
            console.log(data);
        }

        dispatch(setEventsData({id: 'decline_code', data: txt}));
        sendAnalyticsEvents(EVENTS.subBuyFailed, {...eventsData, declineCode: txt});
        dispatch(setError({show: true, text: txt, type: 'payment'}));
    };
    const onSuccess = ({ customer_id, subscription_id }) => {
        const localData = {
            customer_id,
            subscription_id,
            plan_id: plan,
        };
        dispatch(setRequestResult({isSuccess: true, id: subscription_id}));
        dispatch(setEventsData(localData));
        sendAnalyticsEvents(EVENTS.subscribed, {...eventsData, ...localData});
    };

    const purchaseRequest = await getSubscriptionID(nonce, plan, token, analyticsParams, eventsData, eventId);

    try {
        const { errors, data } = purchaseRequest;
        if (errors) {
            onError(errors?.messages || 'An error has occurred', data);
            return;
        }
        onSuccess(data);
    } finally {
        dispatch(setCheckoutSpinner({show: false, type: ''}));
    }
};

export const selectPlan = state => state.paywall.plan;
export const selectSuccess = state => state.paywall.isSuccess;
export const selectCardName = state => state.paywall.cardholderName;
export const selectFormValidity = state => state.paywall.isFormValid;
export const selectID = state => state.paywall.id;
export const selectFinalPrice = state => state.paywall.finalPrice;
export const selectDiscountAmount = state => state.paywall.discountPercentage;
export const selectUpsellData = state => state.paywall.upsellData;
export const selectStripeNonce = state => state.paywall.stripeNonce;
export const selectUpsellPlan = state => state.paywall.upsellPlan;


export default paywallSlice.reducer;